import React, { useState } from 'react';
import { Container, Row, Col, Form, Card, Spinner, Placeholder, ToastContainer } from 'react-bootstrap';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Toast from 'react-bootstrap/Toast';
import { FaCircleExclamation } from "react-icons/fa6";
import Project from '../Project/Project';
import { searchProjects, getProjectsInBody, getRowsDiscussions, getRowsDiscussionsPO } from '../../service/ApiService';

interface Data {
    projectName: string;
    projectNumber: string;
    daysLeft: number;
    person: string;
    date: Date;
}

interface ResearchField {
    name: keyof Props["filter"];
    label: string;
    type: string;
}

interface Props {
    data: Data[];
    filter: {
        name: string;
        brand: string;
        startDate: string;
        endDate: string;
    };

    onFilterChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    getCurrentConversations: (conversations: any[], isLoading: boolean) => void;
    setProgress: (isLoading: boolean) => void;
    onFilterActivePins: (activePins: any[]) => void;
}

const DataSection: React.FC<Props> = ({ filter, onFilterChange, getCurrentConversations, onFilterActivePins }) => {
    const [progress, setProgress] = useState<boolean>(false);
    const [progressText, setProgressText] = useState<string>('');
    const [fetchProgress, setFetchProgress] = useState<number>(0);
    const [projects, setProjects] = useState<any[]>([]);
    const [initial, setInitial] = useState<boolean>(true);
    const [searchAlertShow, setSearchAlertShow] = useState<boolean>(false);

    // research fields
    const researchFields: ResearchField[] = [
        {
            name: "name",
            label: "Person",
            type: "text",
        },
        {
            name: "brand",
            label: "Brand",
            type: "text",
        },
        {
            name: "startDate",
            label: "Date from",
            type: "date",
        },
        {
            name: "endDate",
            label: "Date to",
            type: "date",
        }
    ]

    const fetchData = async () => {
        try {
            setInitial(false);
            setProgress(true);
            setFetchProgress(0);

            // reset state
            resetData();

            // call project pins search api
            setProgressText('Searching projects...');
            const projectIds = await searchProjects(filter.name, filter.startDate, filter.endDate, filter.brand);
            setFetchProgress(20);
            setProgressText('Fetching discussions feed...');

            // call rows and discussions apis
            const researchData = await getRowsDiscussions(projectIds);
            setFetchProgress(40);

            const researchDataPO = await getRowsDiscussionsPO(projectIds);
            setFetchProgress(70);

            // parse results
            const [sanitizedData, sanitizedDataPO] = sanitizeRows(researchData, researchDataPO);

            // call projects data api
            setFetchProgress(90);
            setProgressText('Fetching projects data...');
            const projectsList = await getProjectsInBody(projectIds);


            // extract discussions
            setFetchProgress(100);
            setProgressText('Data processing...');
            const conversations = extractConversations(researchData, researchDataPO, sanitizedData, sanitizedDataPO);

            // pass discussions to parent
            if (conversations.length) {
                getCurrentConversations(conversations, false);
            }

            // component render
            setProjects(projectsList);

        } catch (error) {

            setProgressText('Error fetching data. Please try again.');
            getCurrentConversations([], false);

        } finally {

            setProgress(false);
            setProgressText('');

        }
    };

    const resetData = () => {
        setProjects([]);
        getCurrentConversations([], true);
    };

    const sanitizeRows = (researchData: any, researchDataPO: any) => {

        const sanitizedData: any[] = [];
        const sanitizedPOData: any[] = [];

        const idColumn = researchData.columns.find((col: any) => col.title === 'ID').id;
        const projectIdColumn = researchData.columns.find((col: any) => col.title === 'Project ID').id;
        const projectTitleColumn = researchData.columns.find((col: any) => col.title === 'ProjectTitle').id;
        const brandColumn = researchData.columns.find((col: any) => col.title === 'Brand').id;
        const brandSubColumn = researchData.columns.find((col: any) => col.title === 'BrandSub').id;

        const projectIdColumnPO = researchDataPO.columns.find((col: any) => col.title === 'Project ID').id;
        const projectTitleColumnPO = researchDataPO.columns.find((col: any) => col.title === 'ProjTitle').id;
        const brandColumnPO = researchDataPO.columns.find((col: any) => col.title === 'ProjGroup').id;
        const brandSubColumnPO = researchDataPO.columns.find((col: any) => col.title === 'SubBrand').id;

        researchData.rows.forEach((row: any) => {
            const rowCells = row.cells;
            const sanitizedRow = {
                id: parseInt(rowCells.find((cell: any) => cell.columnId === idColumn).value),
                po: false,
                messageType: "pm",
                rowId: row.id,
                projectId: rowCells.find((cell: any) => cell.columnId === projectIdColumn).value,
                projectTitle: rowCells.find((cell: any) => cell.columnId === projectTitleColumn).value,
                brand: rowCells.find((cell: any) => cell.columnId === brandColumn).value,
                brandSub: rowCells.find((cell: any) => cell.columnId === brandSubColumn).value,
                permalink: row.permalink,
                createdAt: row.createdAt,
                createdBy: {
                    name: row.createdBy.name,
                    email: row.createdBy.email,
                },
            };
            sanitizedData.push(sanitizedRow);
        });

        researchDataPO.rows.forEach((row: any) => {
            const rowCells = row.cells;
            const sanitizedRowPO = {
                id: parseInt(rowCells.find((cell: any) => cell.columnId === projectIdColumnPO).value),
                po: true,
                messageType: "po",
                rowId: row.id,
                projectId: rowCells.find((cell: any) => cell.columnId === projectIdColumnPO).value,
                projectTitle: rowCells.find((cell: any) => cell.columnId === projectTitleColumnPO).value,
                brand: rowCells.find((cell: any) => cell.columnId === brandColumnPO).value,
                brandSub: rowCells.find((cell: any) => cell.columnId === brandSubColumnPO).value,
                permalink: row.permalink,
                createdAt: row.createdAt,
                createdBy: {
                    name: row.createdBy.name,
                    email: row.createdBy.email,
                },
            };
            sanitizedPOData.push(sanitizedRowPO);
        });

        return [sanitizedData, sanitizedPOData];
    };


    const extractConversations = (researchData: any, researchDataPO: any, matchedRows: any[], matchedRowsPO: any[]) => {
        const conversations: any[] = [];

        researchData.rows.forEach((row: any) => {
            if (Array.isArray(row.discussions)) {
                row.discussions.forEach((conversation: any) => {

                    const projectInfo = matchedRows.find((match: any) => match.rowId === conversation.parentId);
                    conversations.push({
                        ...conversation,
                        projectId: projectInfo.projectId,
                        permalink: projectInfo.permalink,
                        pin: projectInfo.id,
                        po: projectInfo.po,
                        messageType: projectInfo.messageType,
                        brand: projectInfo.brand,
                        brandSub: projectInfo.brandSub,
                        projectName: projectInfo.projectTitle,
                        did: `${projectInfo.id}`
                    });

                });
            }
        });

        researchDataPO.rows.forEach((row: any) => {
            if (Array.isArray(row.discussions)) {
                row.discussions.forEach((conversation: any) => {
                    const projectInfo = matchedRowsPO.find((match: any) => match.rowId === conversation.parentId);
                    conversations.push({
                        ...conversation,
                        projectId: projectInfo.projectId,
                        permalink: projectInfo.permalink,
                        pin: projectInfo.id,
                        po: projectInfo.po,
                        messageType: projectInfo.messageType,
                        brand: projectInfo.brand,
                        brandSub: projectInfo.brandSub,
                        projectName: projectInfo.projectTitle,
                        did: `${projectInfo.id}_po`
                    });

                });
            }
        });

        return conversations;
    };

    const handleSearchSubmit = (e: any) => {
        e.preventDefault();
        if (filter.name === "" && filter.startDate === "" && filter.endDate === "" && filter.brand === "") {
            setSearchAlertShow(true)
            return;
        }

        fetchData();
    };

    const filterActivePins = (pins: any[]) => {
        onFilterActivePins(pins)
    }



    return (
        <>
            <ToastContainer className="p-3"
                position="top-center"
                style={{ zIndex: 1 }} >
                <Toast onClose={() => setSearchAlertShow(false)} show={searchAlertShow} delay={10000} autohide>
                    <Toast.Body>
                        <div className="toast-inner">
                            <div className="toast-icon">
                            <FaCircleExclamation />
                            </div>

                            <div className="toast-text">
                                Fill in at least one of the fields to perform the search
                            </div>
                        </div>
                        <div className="toast-close" onClick={() => setSearchAlertShow(false)} >
                            Ok, got it.
                        </div>
                    </Toast.Body>
                </Toast>
            </ToastContainer>

            <Container fluid className="p-2 search-container">
                <Row>
                    <Col>
                        <h5 className="text-center mb-5">
                            Search projects and discussions
                        </h5>
                    </Col>
                </Row>
                <form onSubmit={(e) => handleSearchSubmit(e)}>
                    <Row>
                        {researchFields.map((field: any, index: number) => {
                            const pickerProp = field.type === "date" ? { onFocus: (e: React.FocusEvent<HTMLInputElement>) => e.currentTarget.showPicker() } : {};

                            return (

                                <Col key={index}>
                                    <Form.Group>
                                        <Form.Label>
                                            {field.label}
                                        </Form.Label>
                                        <Form.Control
                                            name={field.name}
                                            value={filter[field.name as keyof typeof filter] || ''}
                                            onChange={(e: any) => onFilterChange(e)}
                                            type={field.type}
                                            className="rounded-pill"
                                            {...pickerProp}
                                        />
                                    </Form.Group>
                                </Col>
                            )
                        })
                        }

                        <Col>
                            <Form.Label>
                                &nbsp;
                            </Form.Label>
                            <button className="btn btn-primary w-100">
                                Search projects
                            </button>
                        </Col>
                    </Row>
                </form>


            </Container>
            <Container fluid className="data-container">
                <Card.Body>
                    {progress ? (
                        <>
                            <div className="text-center">
                                <Spinner animation="grow" variant="success" style={{ marginBottom: '20px' }}>
                                </Spinner>
                                <div>
                                    Please wait while fetching data
                                </div>
                                <div className="progress-bar-container">
                                    <ProgressBar now={fetchProgress} />
                                </div>
                                <small className="text-muted">{progressText}</small>
                            </div>
                            <Row className="mb-4 mt-5">
                                {Array.from({ length: 4 }).map((_, index) => (
                                    <Col md={6} key={index} >
                                        <Card className="data-card mb-4">

                                            <Card.Body>
                                                <Placeholder as={Card.Text} animation="wave">
                                                    <Placeholder xs={12} size="lg" />
                                                </Placeholder>
                                                <Placeholder as={Card.Text} animation="wave" style={{ marginTop: '40px', marginBottom: '40px', display: 'flex', justifyContent: 'space-around' }}>
                                                    <Placeholder xs={3} size="sm" />
                                                    <Placeholder xs={3} size="sm" />
                                                    <Placeholder xs={3} size="sm" />
                                                </Placeholder>
                                                {Array.from({ length: 5 }).map((_, index) => (
                                                    <div key={index}>
                                                        <Placeholder animation="wave" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-around' }}>
                                                            <Placeholder xs={3} size="xs" />
                                                            <Placeholder xs={3} size="xs" />
                                                            <Placeholder xs={3} size="xs" />
                                                        </Placeholder>
                                                    </div>
                                                ))}

                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}

                            </Row>

                        </>
                    ) : (
                        <Project projects={projects} progress={progress} initial={initial} onFilterActivePins={filterActivePins} />
                    )}
                </Card.Body>
            </Container>
        </>
    );
};

export default DataSection;